import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';

import { verificationSchema, type VerificationValues } from './schemas';

interface VerificationFormProps {
  onSubmit: (values: VerificationValues) => void;
  onResend: () => void;
  /** Back to the email and password, for a mistyped address or a lost code. */
  onStartAgain: () => void;
  pending: boolean;
  resending: boolean;
  /** True once a fresh code has been sent, so the person is told it happened. */
  resent?: boolean;
}

export function VerificationForm({
  onSubmit,
  onResend,
  onStartAgain,
  pending,
  resending,
  resent = false,
}: VerificationFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationValues>({
    resolver: zodResolver(verificationSchema),
    defaultValues: { code: '' },
  });

  return (
    <Box
      component="form"
      onSubmit={(event) => void handleSubmit(onSubmit)(event)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      noValidate
      data-testid="sign-in-code-form"
    >
      <Field
        label="Verification code"
        error={errors.code?.message}
        testId="sign-in-code-code"
      >
        <TextField
          // inputMode brings up a number pad on a phone without rejecting a
          // pasted code the way type="number" would.
          inputMode="numeric"
          autoComplete="one-time-code"
          // The backend's codes are 4 to 10 digits, so there is nothing to type past ten.
          slotProps={{ htmlInput: { maxLength: 10 } }}
          {...register('code')}
        />
      </Field>

      <Button type="submit" loading={pending} data-testid="sign-in-code-submit-button">
        Verify
      </Button>

      <Button
        variant="ghost"
        size="sm"
        onClick={onResend}
        loading={resending}
        data-testid="sign-in-code-resend-button"
      >
        Send the code again
      </Button>

      {/* Always there: somebody who mistyped their address, or whose code
          went to an old mailbox, had no way back to the password step
          (portal-session-rules, B16, D18). */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onStartAgain}
        data-testid="sign-in-code-start-again-button"
      >
        Start again
      </Button>

      {/* A new code really was issued before, but nothing said so, so people
          pressed the button again or kept typing the code it had just retired.
          role="status" means a screen reader is told as well. */}
      {resent ? (
        <Typography
          variant="body2"
          color="text.secondary"
          role="status"
          align="center"
          data-testid="sign-in-code-resent-message"
        >
          A new code is on its way. The previous one no longer works.
        </Typography>
      ) : null}
    </Box>
  );
}
